<template>
    <Transition name="toast-fade" @after-leave="onAfterLeave">
        <div
            v-if="visible && currentFlash"
            dusk="toast-notification"
            class="toast-message"
            :class="messageClass"
        >
            <div class="toast-progress-bar" :class="progressBarClass"></div>
            {{ currentFlash.message }}
        </div>
    </Transition>
</template>

<script lang="ts" setup>
import { usePage } from '@inertiajs/vue3';
import { ref, watch, computed } from 'vue';

interface FlashPayload {
    type: string;
    message: string;
}

const emit = defineEmits<{
    'update:type': [type: string | null];
}>();

const page = usePage();

// Local state — decoupled from `page.props.flash` so Inertia's prop
// merging quirks (sometimes mutating in place vs. replacing the ref)
// don't cause us to miss a toast. Each new flash from the server gets
// snapshotted into `currentFlash`, the timer drives visibility, and
// transition leave-end resets state. We don't mutate page.props.flash
// — the next Inertia visit overwrites it naturally.
const visible = ref(false);
const currentFlash = ref<FlashPayload | null>(null);
let closeTimer: ReturnType<typeof setTimeout> | null = null;

const messageClass = computed(() =>
    currentFlash.value?.type ? `toast-message--${currentFlash.value.type}` : '',
);

const progressBarClass = computed(() =>
    currentFlash.value?.type ? `toast-progress-bar--${currentFlash.value.type}` : '',
);

const showFlash = (flash: FlashPayload) => {
    currentFlash.value = { type: flash.type, message: flash.message };
    visible.value = true;
    emit('update:type', flash.type);

    if (closeTimer !== null) {
        clearTimeout(closeTimer);
    }
    closeTimer = setTimeout(() => {
        visible.value = false;
        closeTimer = null;
    }, 2000);
};

const onAfterLeave = () => {
    currentFlash.value = null;
    emit('update:type', null);
};

watch(
    () => page.props.flash,
    (raw) => {
        const flash = raw as FlashPayload | null | undefined;
        if (flash?.message) {
            showFlash(flash);
        }
    },
    { deep: true, immediate: true },
);
</script>
